'use client';

import getImages from '@/actions/services/getImages';
import ArrowRight from '@/assets/svg/ArrowRight';
import { VideoPlayer } from '@/components/ui/video-player';
import { useWindowWidth } from '@/lib/useWindowWidth';
import { StrapiImage } from '@/types/img';
import { Service } from '@/types/service';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { date } from 'zod';

const largeSizes = [
  {
    w: 799,
    h: 911,
  },
  {
    w: 782,
    h: 571,
  },
  {
    w: 782,
    h: 1039,
  },
  {
    w: 799,
    h: 698,
  },
  {
    w: 1600,
    h: 760,
  },
  {
    w: 644,
    h: 412,
  },
];

const middleSizes = [
  {
    w: 700,
    h: 760, // среднее между 911 и 610
  },
  {
    w: 630,
    h: 476, // среднее между 571 и 382
  },

  {
    w: 680,
    h: 868, // среднее между 1039 и 696
  },
  {
    w: 650,
    h: 582, // среднее между 698 и 467
  },

  {
    w: 800,
    h: 590, // среднее между 760 и 590
  },
  {
    w: 530,
    h: 344, // среднее между 412 и 276
  },
];

const smallSizes = [
  {
    w: 535,
    h: 610,
  },
  {
    w: 523,
    h: 382,
  },
  {
    w: 523,
    h: 696,
  },
  {
    w: 535,
    h: 467,
  },
  {
    w: 628,
    h: 590,
  },
  {
    w: 431,
    h: 276,
  },
];
interface Props {
  service: Service;
  button: string;
  locale: string;
  slug: string;
}

export default function ImagesSectionLong({
  service,
  button,
  locale,
  slug,
}: Props) {
  const [images, setImages] = useState<
    {
      id: number;
      indexId: number;
      img: StrapiImage;
      media: 'image' | 'video';
    }[]
  >([]);
  const width = useWindowWidth();

  useEffect(() => {
    getImages({
      locale: locale,
      slug,
      baseUrl: true,
    }).then((res) => {
      if (res.data?.data) {
        setImages(
          res.data.data[0].images.sort((a, b) => a.indexId - b.indexId)
        );
      }
    });
  }, [service]);

  const sizes =
    width >= 1650 ? largeSizes : width >= 1400 ? middleSizes : smallSizes;

  return (
    <div className='px-28 sm:px-0 2xl:pl-[160px] 2xl:pr-[160px] pr-[90px] pl-[90px]'>
      <div className='flex text-black sm:px-0 sm:pb-10 sm:pt-0 py-20 justify-between gap-12'>
        <div className='flex flex-col gap-16 sm:gap-8'>
          <div className='grid grid-cols-3 sm:grid-cols-1 sm:px-4 gap-24 sm:items-start sm:gap-4 items-start w-full'>
            <h2 className='2xl:text-[55px] text-[36px] col-span-2 sm:col-span-1 sm:text-[24px] leading-[110%] text-[#0D1524] sm:leading-[110%] sm:w-full font-normal sm:mt-0'>
              {service.title}
            </h2>
            <p className='2xl:text-[18px] text-[14px] sm:text-[15px] sm:leading-[110%] mt-2 text-[#0D1524] leading-[113%]'>
              {service.shortDesc}
            </p>
          </div>
          <div className='sm:grid hidden '>
            {images.slice(0, 1).map((i) => (
              <>
                {i?.media === 'video' ? (
                  <video
                    src={
                      typeof i.img?.url === 'string'
                        ? i.img.url
                        : (i.img?.url as { src?: string })?.src ?? ''
                    }
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload='auto'
                    className='masonry-item-service-mobile object-cover rounded-[20px]'
                  />
                ) : i?.img?.url && i?.img?.width && i?.img?.height ? (
                  <Image
                    src={i.img.url}
                    width={i.img.width}
                    height={i.img.height}
                    className='masonry-item-service-mobile object-cover rounded-[20px]'
                    alt=''
                    unoptimized
                    onContextMenu={(e) => e.preventDefault()}
                  />
                ) : null}
              </>
            ))}
          </div>
          <div className='sm:grid hidden px-4 mb-[10px] '>
            <p className='text-[15px] leading-[120%] uppercase font-bold  mb-[21px]'>
              About the service
            </p>
            <p className='font-poppins text-[15px] '>
              {service.about_services}
            </p>
          </div>

          <div className='sm:px-4 flex justify-between gap-10 flex-wrap sm:flex-col'>
            <div className='sm:grid hidden '>
              {images.slice(1, 2).map((i) => (
                <>
                  {i?.media === 'video' ? (
                    <video
                      src={
                        typeof i.img?.url === 'string'  
                          ? i.img.url
                          : (i.img?.url as { src?: string })?.src ?? ''
                      }
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload='auto'
                      className='object-cover rounded-[20px]'
                    />
                  ) : i?.img?.url && i?.img?.width && i?.img?.height ? (
                    <Image
                      src={i.img.url}
                      width={i?.img.width}
                      height={i?.img.height}
                      className='object-cover rounded-[20px]'
                      alt=''
                      onContextMenu={(e) => e.preventDefault()}
                      unoptimized
                    />
                  ) : null}
                </>
              ))}
            </div>
            {/* Benefits */}
            <div className='max-w-[400px]'>
              <p className='2xl:text-[30px] text-[20px] sm:text-[15px] leading-[120%] uppercase font-bold mb-6'>
                Benefits
              </p>
              <div className='flex flex-col gap-1'>
                {service.Benefits?.map((i) => (
                  <div
                    key={i.id}
                    className='border rounded-[80px] w-max border-[#0D1524] px-4 py-1 2xl:text-[20px] text-[13px] sm:text-[13px] leading-[120%] uppercase'
                  >
                    {i.name}
                  </div>
                ))}
              </div>
            </div>

            {/* About the Service */}
            <div className='2xl:max-w-[773px] w-[517px] sm:hidden '>
              <p className='2xl:text-[30px] text-[20px] sm:text-[15px] leading-[120%] uppercase font-bold mb-[14px]'>
                About the service
              </p>
              <p className='font-poppins 2xl:text-[20px] text-[13px] sm:text-[14px]'>
                {service.about_services}
              </p>
            </div>
          </div>
          <div className='sm:grid hidden'>
            {images.slice(2, images.length - 1).map((i) => (
              <>
                {i?.media === 'video' ? (
                  <video
                    src={
                      typeof i.img?.url === 'string'
                        ? i.img.url
                        : (i.img?.url as { src?: string })?.src ?? ''
                    }
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload='auto'
                    className='masonry-item-service-mobile object-cover rounded-[20px]'
                  />
                ) : i?.img?.url && i?.img?.width && i?.img?.height ? (
                  <Image
                    src={i.img.url}
                    width={i.img.width}
                    height={i.img.height}
                    className='masonry-item-service-mobile object-cover rounded-[20px]'
                    alt=''
                    onContextMenu={(e) => e.preventDefault()}
                    unoptimized
                  />
                ) : null}
              </>
            ))}
          </div>
        </div>
      </div>
      <div className='flex justify-center w-full h-full'>
        <section className={`sm:hidden masonry-gallery-service`}>
          {images.map((i, idx) => {
            const { w, h } = sizes[idx];

            return (
              <>
                {i?.media === 'video' ? (
                  // <video
                  //   src={i.img?.url}
                  //   autoPlay
                  //   loop
                  //   onContextMenu={(e) => e.preventDefault()}
                  //   playsInline
                  //   preload='auto'
                  //   width={w}
                  //   height={h}
                  //   className='w-full h-full object-cover rounded-[20px]'
                  // />

                  <VideoPlayer
                    src={
                      typeof i.img?.url === 'string'
                        ? i.img.url
                        : (i.img?.url as { src?: string })?.src ?? ''
                    }
                    width={w}
                    height={h}
                    className='masonry-item-service'
                    key={idx}
                  />
                ) : i?.img?.url ? (
                  <div className='masonry-item-service ' key={idx}>
                    <Image
                      src={i.img.url}
                      width={w}
                      height={h}
                      className='w-full h-full object-cover rounded-[20px]'
                      alt=''
                      onContextMenu={(e) => e.preventDefault()}
                      unoptimized
                    />
                  </div>
                ) : null}
              </>
            );
          })}
          <div
            className={`masonry-item-service ${
              width <= 1650 ? 'w-[431px] h-[233px]' : 'w-[] h-[]'
            } sm:text-[15.94px] sm:tracking-normal sm:leading-none tracking-[-0.05em] sm:gap-2 flex justify-center items-center gap-4 leading-none font-normal`}
          >
            <Link href='/contact' className='inline-block w-fit'>
              <div className='flex gap-6 items-center w-fit'>
                <div className='hover:underline 2xl:text-[72px] text-[48px] sm:text-[38.25px] text-black leading-none tracking-[-0.05em]'>
                  {button}
                </div>
                <div className='2xl:w-[49px] 2xl:h-[40px] w-[32px] h-[26px] mt-2 2xl:h-[40px] sm:w-[26px] sm:h-[21.25px]'>
                  <ArrowRight className='fill-black' />
                </div>
              </div>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
